import type { FictionalRule, FictionalRuleFormValues } from "../../../models/rulesFictional";
import {
  GRUPS_RULES_TENANT_ID,
  toApiRulesTenantId,
  toFormRulesTenantId,
} from "../constants/rulesTenant";
import type { AdminRulesColumnDef, AdminRulesInlineFormField, AdminRulesTabConfig } from "../types";
import {
  formatDisplacementRateLabel,
  fromApiDisplacementRate,
  getDisplacementRateHelperText,
  getDisplacementRateOptions,
  toApiDisplacementRate,
} from "./displacementRate";

export const toApiTenantId = toApiRulesTenantId;

export const INITIAL_FICTIONAL_MAX = 50;
export const MIN_FICTIONAL_MAX = 20;
export const INITIAL_FICTIONAL_DEFAULT = 5;
export const MIN_FICTIONAL_DEFAULT = 0;
const CAPACITY_WARNING_RATIO = 0.8;

const getCapacityWarningThreshold = (capacity: number): number =>
  Math.floor(capacity * CAPACITY_WARNING_RATIO);

export const shouldWarnInitialFictionalCapacity = (
  initialFictional: number,
  capacity: number
): boolean => capacity > 0 && initialFictional > getCapacityWarningThreshold(capacity);

export const ficticiosTabConfig: AdminRulesTabConfig<FictionalRule, FictionalRuleFormValues> = {
  category: "fictional",
  label: "Ficticios",
  enabled: true,
  scopeFields: {
    eventTypeRequired: true,
    tenantRequired: false,
    tenantPersistence: "nullable-grups-sentinel",
  },
  customColumns: [
    {
      field: "initialFictional",
      header: "Ficticios iniciales",
      sortable: true,
    },
    {
      field: "displacementRate",
      header: "Rate de desplazamiento",
      body: (row) => formatDisplacementRateLabel(row.displacementRate),
      sortable: true,
    },
    {
      field: "minFictional",
      header: "Mínimo de ficticios",
      sortable: true,
    },
  ] satisfies AdminRulesColumnDef<FictionalRule>[],
  inlineFormFields: [
    {
      name: "initialFictional",
      label: "Ficticios iniciales",
      type: "number",
      required: true,
      min: 0,
      max: INITIAL_FICTIONAL_MAX,
    },
    {
      name: "displacementRate",
      label: "Rate de desplazamiento",
      type: "dropdown",
      required: true,
      optionLabel: "label",
      optionValue: "value",
      getOptions: (formValues) => getDisplacementRateOptions(formValues.displacementRate),
      getHelperText: (formValues) =>
        getDisplacementRateHelperText(formValues.displacementRate),
    },
    {
      name: "minFictional",
      label: "Mínimo de ficticios",
      type: "number",
      required: true,
      min: 0,
      max: MIN_FICTIONAL_MAX,
    },
  ] satisfies AdminRulesInlineFormField<FictionalRuleFormValues>[],
  getEmptyFormValues: () => ({
    eventTypeId: undefined,
    eventSubTypeId: undefined,
    tenantId: GRUPS_RULES_TENANT_ID,
    initialFictional: INITIAL_FICTIONAL_DEFAULT,
    displacementRate: 1,
    minFictional: MIN_FICTIONAL_DEFAULT,
    active: true,
  }),
  mapRowToFormValues: (row) => ({
    eventTypeId: row.eventTypeId,
    eventSubTypeId: row.eventSubTypeId ?? null,
    tenantId: toFormRulesTenantId(row.tenantId),
    initialFictional: row.initialFictional,
    displacementRate: fromApiDisplacementRate(row.displacementRate),
    minFictional: row.minFictional,
    active: row.active,
  }),
  mapFormToCreatePayload: (values) => ({
    eventTypeId: Number(values.eventTypeId),
    eventSubTypeId: values.eventSubTypeId ?? null,
    tenantId: toApiTenantId(values.tenantId),
    initialFictional: Number(values.initialFictional),
    displacementRate: toApiDisplacementRate(values.displacementRate),
    minFictional: Number(values.minFictional ?? 0),
    active: Boolean(values.active),
  }),
  mapFormToUpdatePayload: (values, rowId) => ({
    id: rowId,
    eventTypeId: Number(values.eventTypeId),
    eventSubTypeId: values.eventSubTypeId ?? null,
    tenantId: toApiTenantId(values.tenantId),
    initialFictional: Number(values.initialFictional),
    displacementRate: toApiDisplacementRate(values.displacementRate),
    minFictional: Number(values.minFictional ?? 0),
    active: Boolean(values.active),
  }),
  validateForm: (values) => {
    if (!values.eventTypeId) return "Seleccioná un tipo de evento.";
    if (values.initialFictional == null || Number.isNaN(Number(values.initialFictional))) {
      return "Ingresá ficticios iniciales.";
    }
    const initialFictional = Number(values.initialFictional);
    if (initialFictional < 0 || initialFictional > INITIAL_FICTIONAL_MAX) {
      return `Los ficticios iniciales deben estar entre 0 y ${INITIAL_FICTIONAL_MAX}.`;
    }
    const displacementRate = Number(values.displacementRate);
    if (
      values.displacementRate == null ||
      !Number.isInteger(displacementRate) ||
      displacementRate < 1
    ) {
      return "Seleccioná un rate de desplazamiento válido.";
    }
    const minFictional = Number(values.minFictional ?? 0);
    if (Number.isNaN(minFictional) || minFictional < 0 || minFictional > MIN_FICTIONAL_MAX) {
      return `El mínimo de ficticios debe estar entre 0 y ${MIN_FICTIONAL_MAX}.`;
    }
    if (initialFictional < minFictional) {
      return "Los ficticios iniciales no pueden ser menores al mínimo.";
    }
    return null;
  },
};
